import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import ModalDialog, {
	ModalTitle,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Box, Inline, Text } from '@atlaskit/primitives';

import {
	GeneralAccess,
	type GeneralAccessProps,
} from '../shareAndRestrictDialog/header/generalAccess/GeneralAccess';

const i18n = defineMessages({
	title: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.title',
		defaultMessage: 'Open up access to everyone',
		description:
			'Title for the open access warning dialog which appears when the user tries to open up access to everyone on a Free edition site',
	},
	body: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.body',
		defaultMessage:
			'<b>This can’t be undone on the Free plan.</b> This will restore the content’s access level to the following default level:',
		description:
			'Body text for the open access warning dialog which appears when the user tries to open up access to everyone on a Free edition site',
	},
	cancelButton: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.cancelButton',
		defaultMessage: 'Cancel',
		description: 'Cancel button text for the open access warning dialog',
	},
	openAccessButton: {
		id: 'share-and-restrict-dialog.openAccessWarningDialog.openAccessButton',
		defaultMessage: 'Open up access',
		description: 'Confirmation button text for the Open access warning dialog',
	},
});

export type OpenAccessWarningDialogProps = {
	generalAccess: GeneralAccessProps;
	onOpenAccess: () => void;
	onCancel: () => void;
};

export const OpenAccessWarningDialog: FC<OpenAccessWarningDialogProps> = ({
	generalAccess,
	onCancel,
	onOpenAccess,
}) => {
	return (
		<ModalDialog width="small">
			<ModalHeader>
				<ModalTitle appearance="warning">Open up access to everyone</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Box paddingBlockEnd="space.200">
					<FormattedMessage
						{...i18n.body}
						values={{ b: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text> }}
					/>
				</Box>
				<GeneralAccess {...generalAccess} showBottomPadding />
			</ModalBody>
			<ModalFooter>
				<Inline alignBlock="center" alignInline="end">
					<Inline alignBlock="center" alignInline="center" space="space.100">
						<Button appearance="default" onClick={onCancel}>
							<FormattedMessage {...i18n.cancelButton} />
						</Button>
						<Button appearance="warning" onClick={onOpenAccess}>
							<FormattedMessage {...i18n.openAccessButton} />
						</Button>
					</Inline>
				</Inline>
			</ModalFooter>
		</ModalDialog>
	);
};
